import { useEffect, useState } from "react";
import { FormDialog, Field } from "./dreamShared";
import { MELT_INPUT, MELT_TEXTAREA } from "./ui/melt";
import { DatePicker } from "./ui/date-picker";

export type TargetRole = {
  id: string;
  title: string;
  location?: string;
  url?: string;
  deadline?: string; // yyyy-mm-dd
  notes?: string;
  applied?: boolean;
  trackerId?: string;
};

type AddTargetRoleProps = {
  isOpen: boolean;
  companyName: string;
  editRole?: TargetRole | null;
  onClose: () => void;
  onSave: (role: TargetRole) => void;
};

export default function AddTargetRole({
  isOpen,
  companyName,
  editRole,
  onClose,
  onSave,
}: AddTargetRoleProps) {
  const [title, setTitle] = useState("");
  const [location, setLocation] = useState("");
  const [url, setUrl] = useState("");
  const [deadline, setDeadline] = useState("");
  const [notes, setNotes] = useState("");

  useEffect(() => {
    if (editRole) {
      setTitle(editRole.title);
      setLocation(editRole.location || "");
      setUrl(editRole.url || "");
      setDeadline(editRole.deadline || "");
      setNotes(editRole.notes || "");
    } else {
      setTitle("");
      setLocation("");
      setUrl("");
      setDeadline("");
      setNotes("");
    }
  }, [editRole, isOpen]);

  const handleSubmit = () => {
    const role: TargetRole = {
      id: editRole?.id || Date.now().toString(),
      title: title.trim(),
      ...(editRole?.applied ? { applied: true } : {}),
      ...(editRole?.trackerId ? { trackerId: editRole.trackerId } : {}),
    };
    if (location.trim()) role.location = location.trim();
    if (url.trim()) role.url = url.trim();
    if (deadline) role.deadline = deadline;
    if (notes.trim()) role.notes = notes.trim();

    onSave(role);
    onClose();
  };

  return (
    <FormDialog
      open={isOpen}
      onClose={onClose}
      title={editRole ? "Edit role" : "Add role"}
      description={`A role you’re watching at ${companyName}.`}
      submitLabel={editRole ? "Save changes" : "Add role"}
      canSubmit={Boolean(title.trim())}
      onSubmit={handleSubmit}
    >
      <Field id="role-title" label="Role title" required>
        <input
          id="role-title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="e.g. New Grad Software Engineer"
          className={MELT_INPUT}
          autoFocus
        />
      </Field>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field id="role-location" label="Location">
          <input
            id="role-location"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            placeholder="e.g. Remote / Toronto"
            className={MELT_INPUT}
          />
        </Field>
        <Field id="role-deadline" label="Deadline">
          <DatePicker id="role-deadline" value={deadline} onChange={setDeadline} className={MELT_INPUT} />
        </Field>
      </div>

      <Field id="role-url" label="Job posting">
        <input
          id="role-url"
          type="url"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://…"
          className={MELT_INPUT}
        />
      </Field>

      <Field id="role-notes" label="Notes">
        <textarea
          id="role-notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Referral contact, team, requirements to check…"
          rows={3}
          className={MELT_TEXTAREA}
        />
      </Field>
    </FormDialog>
  );
}
